import { useState, useEffect, useCallback } from "react";
import { apiFetchLiveVideoAdCampaigns } from "../../api/backend/campaign";

export const useAdCampaigns = () => {
  // Retrieve any stored counter from localStorage, defaulting to 0

  const MAX_INITIAL_FETCH_ATTEMPTS = 3;

  const getInitialFetchAttempts = () => {
    return parseInt(localStorage.getItem("adsFetchAttempts"), 10) || 0;
  };

  const incrementInitialFetchAttempts = () => {
    const currentAttempts = getInitialFetchAttempts();
    const newAttempts = currentAttempts + 1;
    localStorage.setItem("adsFetchAttempts", newAttempts);
    return newAttempts;
  };

  const resetInitialFetchAttempts = () => {
    localStorage.removeItem("adsFetchAttempts");
  };

  const [ads, setAds] = useState(
    () => JSON.parse(localStorage.getItem("ads")) || []
  );
  const [adPointer, setAdPointer] = useState(
    () => parseInt(localStorage.getItem("adPointer"), 10) || 0
  );

  // Persist ads and pointer in localStorage
  useEffect(() => {
    localStorage.setItem("ads", JSON.stringify(ads));
  }, [ads]);

  useEffect(() => {
    localStorage.setItem("adPointer", adPointer.toString());
  }, [adPointer]);

  // If no campaigns are returned (campaigns.length === 0),
  // the function recursively calls itself without a lastId
  // since last id is hard coded and set to null on backend then
  // the database will call the first record of the ad
  const fetchCampaigns = useCallback(async (lastId = null) => {
    try {
      const response = await apiFetchLiveVideoAdCampaigns(lastId, 10);
      console.log("Full response:", response);
      const { initializedType, campaigns, message } = response;

      if (lastId === null) {
        // Initial fetch: increment the counter
        const attempts = incrementInitialFetchAttempts();

        if (initializedType === "live") {
          // Successful live campaigns found
          const newAds = campaigns.map((ad) => ({
            adId: ad.id,
            mediaAdUrl: ad.mediaAdUrl,
            campaignAdUrl: ad.campaignAdUrl,
            campaignName: ad.campaignName, // Correctly assign the ad title from the backend.
            targetId: ad.sourceId,
          }));
          setAds(newAds);
          setAdPointer(0);
          // Optionally reset the attempts counter since we got a successful live result.
          resetInitialFetchAttempts();
        } else if (initializedType === "empty") {
          console.log(message);
          return;
        }

        // If we have attempted MAX_INITIAL_FETCH_ATTEMPTS without success, stop refetching
        if (attempts >= MAX_INITIAL_FETCH_ATTEMPTS) {
          console.log(
            "Reached maximum initial fetch attempts. Stopping further fetches."
          );
          return;
        }
      } else {
        // For paginated fetches
        if (campaigns.length > 0) {
          const newAds = campaigns.map((ad) => ({
            adId: ad.id,
            mediaAdUrl: ad.mediaAdUrl,
            campaignAdUrl: ad.campaignAdUrl,
            campaignName: ad.campaignName, // Correctly assign the ad title from the backend.
            targetId: ad.sourceId,
          }));
          setAds((prevAds) => [...prevAds, ...newAds]);
        } else {
          console.log(
            "No campaigns found on paginated fetch. Stopping further fetches."
          );
          return;
        }
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  }, []);

  // Get the next ad
  const getNextAd = useCallback(() => {
    if (ads.length === 0) {
      console.warn("No ads in local storage.");
      return null;
    }
    // Get the current pointer from localStorage if needed
    const storedPointer =
      parseInt(localStorage.getItem("adPointer"), 10) || adPointer;
    const nextAd = ads[storedPointer];
    const newPointer = (storedPointer + 1) % ads.length;

    console.log("Before Increment:", adPointer);

    // Fetch next batch of ads when pointer loops back
    if (newPointer === 0) {
      fetchCampaigns(ads[ads.length - 1]?.adId); // Fetch new ads
    }

    setAdPointer(newPointer); // Increment pointer globally
    console.log("After Increment:", newPointer);

    localStorage.setItem("adPointer", newPointer.toString()); // Sync with local storage

    return nextAd;
  }, [ads, adPointer, fetchCampaigns]);

  // Initialize ads on mount if empty
  useEffect(() => {
    // Only call fetchCampaigns if there are no ads and we haven't reached the max attempts
    if (
      ads.length === 0 &&
      getInitialFetchAttempts() < MAX_INITIAL_FETCH_ATTEMPTS
    ) {
      fetchCampaigns();
    }
  }, [fetchCampaigns, ads]);

  return { ads, getNextAd, fetchCampaigns };
};
